if (!customElements.get('before-after')) {
  class BeforeAfter extends HTMLElement {
    constructor() {
      super();
      // Query selectors for necessary elements
      this.texts = this.querySelectorAll('[data-text]');
      this.before = this;
      this.after = this.querySelector('[data-after]');
      this.divider = this.querySelector('[data-divider]');
      this.isDragging = false;
      this.isAnimated = false;

      // Event listeners for dragging functionality (Mouse and Touch) on the divider
      this.addEventListener('mousedown', this.startDrag.bind(this));
      this.addEventListener('mousemove', this.onDrag.bind(this));
      this.addEventListener('mouseup', this.stopDrag.bind(this));

      // Touch events for mobile support on the divider
      this.addEventListener('touchstart', this.startDrag.bind(this));
      this.addEventListener('touchmove', this.onDrag.bind(this));
      this.addEventListener('touchend', this.stopDrag.bind(this));

      // Initial setup
      this.compareElements();

      // Setup intersection observer
      this.setupObserver();
    }

    // Initial setup of the divider position
    compareElements() {
      this.updateDivider(0); // Start with divider at left (0%)
    }

    // Setup intersection observer to trigger animation when element comes into view
    setupObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isAnimated) {
            this.animateDividerToCenter();
            this.isAnimated = true;
          }
        });
      }, {
        threshold: 0.5 // Trigger when 50% of the element is visible
      });

      observer.observe(this);
    }

    // Animate divider from left to center
    animateDividerToCenter() {
      let start = 0;
      const end = 50;
      const duration = 1500; // 1.5 second animation
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);

        const currentPercentage = start + (end - start) * easeOutQuart;

        this.updateDivider(currentPercentage);
        this.after.style.clipPath = `inset(0 ${100 - currentPercentage}% 0 0)`;
        this.checkTextVisibility(currentPercentage);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }

    // Starts the dragging process (Handles both mouse and touch)
    startDrag(e) {
      e.stopPropagation();
      this.isDragging = true;
      this.onDrag(e); // Update immediately on mousedown/touchstart
    }

    // Handles the dragging movement (Supports both mouse and touch)
    onDrag(e) {
      e.stopPropagation();
      if (this.isDragging) {
        let clientX;

        // Determine the clientX position based on mouse or touch event
        if (e.type === 'mousemove') {
          clientX = e.clientX;
          console.log('Mouse move detected:', clientX);
        } else if (e.type === 'touchmove') {
          clientX = e.touches[0].clientX;
        }

        if (clientX) {
          // Get the bounding rectangle of the before element
          const rect = this.before.getBoundingClientRect();
          // Calculate the mouse/touch position relative to the element
          const positionX = clientX - rect.left;
          const width = rect.width;

          // Add 20px padding on both sides
          const padding = this.divider.firstElementChild.clientWidth / 2;
          const minPosition = padding;
          const maxPosition = width - padding;

          // Clamp the position to respect the padding
          const clampedPosition = Math.max(minPosition, Math.min(positionX, maxPosition));

          // Calculate the percentage based on the clamped position
          const percentage = (clampedPosition / width) * 100;

          // The percentage is already clamped by the position constraints
          const clampedPercentage = percentage;

          // Update the clip-path of the after element based on the percentage
          this.after.style.clipPath = `inset(0 ${100 - clampedPercentage}% 0 0)`;
          // Update the divider position based on the percentage
          this.updateDivider(clampedPercentage);
          // Check and update the visibility of text elements
          this.checkTextVisibility(clampedPercentage);
        }
      }
    }

    // Stops the dragging process (Handles both mouse and touch)
    stopDrag(e) {
      e.stopPropagation();
      this.isDragging = false;
    }

    // Updates the position of the divider
    updateDivider(percentage) {
      this.divider.style.left = `${percentage}%`;
    }

    // Checks if the divider overlaps any text elements and updates their data-active attribute
    checkTextVisibility(percentage) {
      // Calculate the position of the divider based on the percentage
      const dividerPosition = (percentage / 100) * this.before.clientWidth;

      this.texts.forEach(textElement => {
        // Get the bounding rectangle of the text element and the before element
        const textRect = textElement.getBoundingClientRect();
        const elementRect = this.before.getBoundingClientRect();

        // Check if the divider overlaps the text element
        const isOverlapping =
          (textRect.left < elementRect.left + dividerPosition &&
            textRect.right > elementRect.left + dividerPosition);

        // Update the data-active attribute based on overlap
        textElement.setAttribute('data-active', !isOverlapping);
      });
    }
  }

  // Define the custom element
  customElements.define('before-after', BeforeAfter);
}
